"use client";

import { X } from "lucide-react";
import { useTranslations } from "next-intl";
import { Link } from "@/i18n/navigation";
import { AnalyticsEvents, trackEvent } from "@/lib/client/analytics/product-analytics";
import { Image } from "@/lib/client/components/Image";
import { useCountryStore, useShoppingCartStore } from "@/lib/client/stores";
import type { CartItemForClient } from "@/lib/shared/types/interfaces";
import { translateProductVariantLabel } from "@/lib/shared/utils/productVariantLabel";
import {
  Button,
  HStack,
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Text,
  VStack,
} from "@/ui";

interface CartItemProps {
  item: CartItemForClient;
}

/**
 * CartItem component - Individual cart item display
 */
export const CartItem = ({ item }: CartItemProps) => {
  const shoppingCartT = useTranslations("shopping_cart");
  const productDetailsT = useTranslations("product_details");
  const { cartItems, setCartItems, removeItem, setIsOpen } = useShoppingCartStore();
  const { country } = useCountryStore();

  const productVariant = item.product.variants.find((v) => v.variant.id === item.variant.id);

  if (!productVariant) return null;

  const handleQuantityChange = (value: string) => {
    const newQuantity = Number.parseInt(value);
    const oldQuantity = item.quantity;

    trackEvent(AnalyticsEvents.CART_QUANTITY_UPDATED, {
      product_id: item.product.id,
      sku: item.product.sku,
      variant_id: item.variant.id,
      old_quantity: oldQuantity,
      new_quantity: newQuantity,
    });

    const updatedCartItems = cartItems.map((cartItem) => {
      if (
        cartItem.product.id === item.product.id &&
        cartItem.variant.id === productVariant.variant.id
      ) {
        return { ...cartItem, quantity: newQuantity };
      }
      return cartItem;
    });
    setCartItems(updatedCartItems);
  };

  const handleRemoveItem = () => {
    trackEvent(AnalyticsEvents.CART_ITEM_REMOVED, {
      product_id: item.product.id,
      sku: item.product.sku,
      variant_id: item.variant.id,
      quantity: item.quantity,
    });
    removeItem(item);
  };

  const generateNumberOptions = (max: number) => {
    return Array.from({ length: Math.min(max, 10) }, (_, i) => i + 1).map((num) => (
      <SelectItem
        key={num}
        value={num.toString()}
      >
        {num}
      </SelectItem>
    ));
  };

  return (
    <VStack>
      <HStack className="py-4 text-sm sm:items-center">
        <div className="relative w-24 flex-shrink-0 sm:w-32">
          <Link
            href={`/products/${item.product.sku}`}
            onClick={() => setIsOpen(false)}
          >
            <div className="relative aspect-square overflow-hidden rounded-md">
              <Image
                src={item.product.images?.[0]?.thumbnailSrc || item.product.images?.[0]?.src}
                alt={item.product.images?.[0]?.alt || item.product.name}
                fill
                className="object-cover"
              />
            </div>
          </Link>
        </div>
        <HStack className="w-full items-center justify-between">
          <VStack className="px-2 sm:px-4">
            <Link href={`/products/${item.product.sku}`}>
              <Text
                className="font-medium hover:link"
                onClick={() => setIsOpen(false)}
              >
                {item.product.name}
              </Text>
            </Link>
            {item.product.variants.length > 1 && (
              <Text className="mt-1 text-foreground/75">
                {translateProductVariantLabel(productDetailsT, productVariant.variant.variant)}
              </Text>
            )}
          </VStack>
          <HStack className="items-center space-x-2">
            <Select
              value={item.quantity.toString()}
              onValueChange={handleQuantityChange}
            >
              <SelectTrigger
                className="min-w-14"
                data-testid={`cart-item-qty-${item.product.sku}`}
              >
                <SelectValue placeholder="number" />
              </SelectTrigger>
              <SelectContent
                className="max-h-52 overflow-y-auto"
                position="popper"
                onWheel={(e) => {
                  e.stopPropagation();
                }}
              >
                <SelectGroup>{generateNumberOptions(productVariant.stock ?? 0)}</SelectGroup>
              </SelectContent>
            </Select>

            {productVariant.onSalePrice ? (
              <VStack>
                <Text className="font-price line-through">
                  {country.symbol}
                  {Number(productVariant.price * item.quantity * country.exchangeRate).toFixed(2)}
                </Text>
                <Text className="font-price text-red-500">
                  {country.symbol}
                  {Number(
                    productVariant.onSalePrice * item.quantity * country.exchangeRate,
                  ).toFixed(2)}
                </Text>
              </VStack>
            ) : (
              <Text className="font-price">
                {country.symbol}
                {Number(productVariant.price * item.quantity * country.exchangeRate).toFixed(2)}
              </Text>
            )}
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={handleRemoveItem}
              aria-label={shoppingCartT("remove")}
              className="h-6 w-6 flex-none text-red-500 transition-colors hover:text-red-600"
            >
              <X className="h-4 w-4" />
            </Button>
          </HStack>
        </HStack>
      </HStack>
    </VStack>
  );
};
